/**
 * Corona, CA service-area pages. Each entry drives one /services/<slug> page
 * (see ServiceAreaPage) and one seoRoutes entry. Copy is service-specific (no
 * shared body text across pages); Corona is mentioned naturally, not stuffed.
 *
 * @typedef {{ title: string, body: string }} Card
 * @typedef {{
 *   slug: string, eyebrow: string, h1: string, title: string, description: string,
 *   heroLead: string, heroPoints: string[],
 *   problemTitle: string, problem: string[],
 *   scopeTitle: string, scope: Card[],
 *   useCasesTitle: string, useCases: Card[],
 *   process: Card[],
 *   localTitle: string, local: string[],
 *   related: string[]
 * }} CoronaService
 */

/** @type {CoronaService[]} */
export const coronaServices = [
  {
    slug: "ai-receptionist-corona",
    eyebrow: "AI receptionist",
    h1: "AI Receptionist Setup in Corona, CA",
    title: "AI Receptionist Setup in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks sets up AI receptionists for Corona, CA small businesses — 24/7 call answering, lead qualification, scheduling, and SMS follow-up.",
    heroLead:
      "Stop sending customers to voicemail. AICSNetworks builds and configures an AI receptionist that answers every call, captures the lead, and follows up — day, night, and weekends — for small businesses in Corona, CA.",
    heroPoints: [
      "Answers 24/7 with scripts and a tone you control",
      "Captures caller name, reason, and callback details",
      "Books or routes appointments and texts a summary to your team",
    ],
    problemTitle: "Every missed call is a missed customer",
    problem: [
      "When your team is on a job, with a customer, or off the clock, calls go to voicemail — and most callers won't leave one. They call the next business instead.",
      "Hiring a full-time receptionist is expensive, and after-hours coverage is harder still. An AI receptionist answers instantly, every time, so the work you already earned doesn't slip away.",
    ],
    scopeTitle: "What AICSNetworks sets up",
    scope: [
      { title: "Call answering & greeting", body: "A natural-sounding assistant that answers on the first ring with your business greeting and brand voice." },
      { title: "Lead qualification & intake", body: "Structured questions that capture who's calling, what they need, urgency, and the best callback details." },
      { title: "Scheduling & routing", body: "Books appointments or routes the caller to the right person, with rules you control for escalation." },
      { title: "SMS & follow-up", body: "Texts a clean summary to your team and follows up with the caller — across phone, SMS, and web chat." },
    ],
    useCasesTitle: "How Corona businesses use it",
    useCases: [
      { title: "Home services & trades", body: "Capture estimate requests with the property address and urgency while crews are on site." },
      { title: "Clinics & offices", body: "Handle appointment requests and common questions without tying up front-desk staff." },
      { title: "After-hours coverage", body: "Answer evening and weekend calls so leads aren't lost until Monday." },
      { title: "Overflow on busy days", body: "Catch calls that would otherwise ring out when everyone is already on the line." },
    ],
    process: [
      { title: "Discovery", body: "We learn your call flow, common questions, and how leads should be handled." },
      { title: "Planning", body: "We script the greeting, intake questions, routing, and escalation rules with you." },
      { title: "Build & setup", body: "We configure the assistant, provision the phone number, and connect SMS follow-up." },
      { title: "Testing", body: "We run real call scenarios and tune the responses until they sound right." },
      { title: "Launch & support", body: "We go live and adjust scripts as your business and busy seasons change." },
    ],
    localTitle: "Set up and supported in Corona, CA",
    local: [
      "AICSNetworks is based in Corona, CA, so setup, tuning, and ongoing changes are handled by a local team that can meet and adjust the assistant as you grow — not a generic call-center bot.",
    ],
    related: ["ai-chatbot-development-corona", "workflow-automation-corona", "it-support-corona"],
  },
  {
    slug: "ai-chatbot-development-corona",
    eyebrow: "AI chatbot",
    h1: "AI Chatbot Development in Corona, CA",
    title: "AI Chatbot Development in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks develops AI chatbots for Corona, CA businesses — grounded in your docs and data to answer customers accurately and capture leads on your website.",
    heroLead:
      "Give website visitors real answers instead of a contact form. AICSNetworks develops AI chatbots for Corona, CA businesses that answer questions from your own content, qualify leads, and hand off to a person when it matters.",
    heroPoints: [
      "Answers grounded in your FAQs, docs, and product info",
      "Qualifies leads and captures contact details",
      "Escalates to a real person when a question needs one",
    ],
    problemTitle: "Visitors want answers now — not a callback",
    problem: [
      "When your site only offers a form, people leave before you reply, and staff lose hours answering the same questions about hours, pricing, services, and availability.",
      "A generic chatbot that makes things up is worse than none. The fix is a chatbot grounded in your real information, with a clean handoff when it reaches its limits.",
    ],
    scopeTitle: "What AICSNetworks builds",
    scope: [
      { title: "Knowledge grounding", body: "We connect the bot to your FAQs, manuals, policies, and site content so answers come from your business — not guesses." },
      { title: "Lead qualification", body: "The assistant asks the right questions and captures contact details for follow-up." },
      { title: "Human handoff", body: "When a request goes beyond what it should answer, it routes to a person or your team inbox." },
      { title: "Website embed", body: "A lightweight widget that installs on your existing site and matches your brand." },
    ],
    useCasesTitle: "Where it helps",
    useCases: [
      { title: "Product & service questions", body: "Answer 'do you do X?', pricing ranges, and availability instantly." },
      { title: "Support intake", body: "Collect the details a support request needs before a human ever steps in." },
      { title: "After-hours capture", body: "Turn late-night visitors into qualified leads waiting for you in the morning." },
      { title: "Reducing repeat email", body: "Deflect the routine questions that clog your inbox so staff focus on real work." },
    ],
    process: [
      { title: "Discovery", body: "We review your site, top questions, and the content the bot should rely on." },
      { title: "Planning", body: "We define scope, guardrails, lead questions, and when to hand off to a person." },
      { title: "Build & setup", body: "We build the assistant, ground it in your content, and prepare the embed." },
      { title: "Testing", body: "We test real questions and tighten answers and handoffs before launch." },
      { title: "Launch & support", body: "We embed it on your site and keep it accurate as your content changes." },
    ],
    localTitle: "Built with a local Corona, CA team",
    local: [
      "Scoping, content review, and tuning happen with a Corona, CA-based team that keeps your chatbot accurate as your offerings and information change.",
    ],
    related: ["ai-receptionist-corona", "business-website-development-corona", "workflow-automation-corona"],
  },
  {
    slug: "business-website-development-corona",
    eyebrow: "Web development",
    h1: "Business Website Development in Corona, CA",
    title: "Business Website Development in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks builds fast, custom business websites for Corona, CA companies — clear service pages, contact forms, mobile-first layouts, and local SEO that converts.",
    heroLead:
      "Your website should win work, not just exist. AICSNetworks designs and builds fast, custom business websites for Corona, CA companies — clear messaging, mobile-first layouts, and local SEO built to turn visitors into leads.",
    heroPoints: [
      "Performance-first build — not template or plugin bloat",
      "Clear service pages, contact forms, and calls to action",
      "Mobile-first layout and local SEO foundations",
    ],
    problemTitle: "A slow or dated site quietly costs you leads",
    problem: [
      "Template builders and plugin-stacked sites get heavy, fragile, and hard to change — and a slow, unclear site loses customers and search ranking.",
      "You need a site that loads fast, reads clearly on a phone, and makes it obvious how to contact you.",
    ],
    scopeTitle: "What AICSNetworks delivers",
    scope: [
      { title: "Custom design", body: "A brand-faithful site built by hand for speed and clarity — not a generic theme." },
      { title: "Service & contact pages", body: "Clear pages for each service and a working contact form that routes leads to you." },
      { title: "Mobile-first & accessible", body: "Layouts that look right on every screen, with clean, readable structure." },
      { title: "Local SEO foundations", body: "Proper titles, headings, structured data, and a sitemap so you can rank locally." },
    ],
    useCasesTitle: "Good fits",
    useCases: [
      { title: "New or rebranded businesses", body: "Launch a credible, fast site that reflects how you actually operate." },
      { title: "Replacing a slow site", body: "Move off a bloated builder to a stack you own and can grow." },
      { title: "Lead-focused service businesses", body: "Make calls, quotes, and bookings the obvious next step on every page." },
      { title: "Local visibility", body: "Show up for Corona-area searches with clean on-page SEO." },
    ],
    process: [
      { title: "Discovery", body: "We learn your services, customers, and the leads the site needs to drive." },
      { title: "Planning", body: "We map the pages, messaging, and structure before any design starts." },
      { title: "Build & setup", body: "We build the site for speed, clarity, and conversion on a stack you own." },
      { title: "Testing", body: "We check performance, mobile layout, forms, and on-page SEO." },
      { title: "Launch & support", body: "We deploy, connect your domain, and support changes as you grow." },
    ],
    localTitle: "For businesses in and around Corona, CA",
    local: [
      "AICSNetworks works with Corona, CA businesses and nearby communities, and can pair the website with AI chat, automation, and IT support when you want more than a brochure site.",
    ],
    related: ["ai-chatbot-development-corona", "custom-software-development-corona", "it-support-corona"],
  },
  {
    slug: "custom-software-development-corona",
    eyebrow: "Custom software",
    h1: "Custom Software Development in Corona, CA",
    title: "Custom Software Development in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks builds custom software for Corona, CA businesses — internal tools, dashboards, integrations, and apps that replace spreadsheets and manual work.",
    heroLead:
      "When off-the-shelf tools don't fit, software should bend to your process — not the other way around. AICSNetworks builds custom internal tools, dashboards, and integrations for businesses in Corona, CA.",
    heroPoints: [
      "Internal tools and apps built around your workflow",
      "Integrations between the systems you already use",
      "Dashboards and reporting from your own data",
    ],
    problemTitle: "Spreadsheets and manual steps don't scale",
    problem: [
      "Disconnected apps, copy-paste between systems, and ad-hoc spreadsheets slow teams down and cause errors as you grow.",
      "Generic software often forces your process to bend around the tool. Custom software fits how your team already works.",
    ],
    scopeTitle: "What AICSNetworks builds",
    scope: [
      { title: "Internal tools", body: "Apps that replace fragile spreadsheets and manual processes your team relies on." },
      { title: "Dashboards & reporting", body: "Clear views of the data you care about, pulled from your real systems." },
      { title: "Integrations & APIs", body: "Connections between the tools you already use so data moves without copy-paste." },
      { title: "Customer-facing apps", body: "Portals and apps your customers use, built to match your brand and workflow." },
    ],
    useCasesTitle: "Common projects",
    useCases: [
      { title: "Replace a spreadsheet", body: "Turn a critical, error-prone spreadsheet into a real tool with validation and access control." },
      { title: "Connect two systems", body: "Sync records between your CRM, scheduling, and back-office tools automatically." },
      { title: "Operational dashboard", body: "See jobs, status, or revenue at a glance instead of digging through apps." },
      { title: "Client portal", body: "Give customers a simple place to view status, submit requests, or upload files." },
    ],
    process: [
      { title: "Discovery", body: "We map your current process and where the friction and errors are." },
      { title: "Planning", body: "We scope the build in clear stages with a defined first release." },
      { title: "Build & setup", body: "We build on a maintainable stack you own — no lock-in." },
      { title: "Testing", body: "We test with real data and your team before anything goes live." },
      { title: "Launch & support", body: "We roll out in stages and stay available as needs change." },
    ],
    localTitle: "Local, accountable delivery in Corona, CA",
    local: [
      "Based in Corona, CA, AICSNetworks scopes the work with you up front, delivers in clear stages, and stays available for changes so the software keeps fitting your business over time.",
    ],
    related: ["workflow-automation-corona", "business-website-development-corona", "it-support-corona"],
  },
  {
    slug: "workflow-automation-corona",
    eyebrow: "Automation",
    h1: "Workflow Automation in Corona, CA",
    title: "Workflow Automation in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks automates repetitive workflows for Corona, CA businesses — intake, ticketing, email, CRM updates, reporting, and follow-up that run themselves.",
    heroLead:
      "Get hours back every week. AICSNetworks helps Corona, CA businesses automate the repetitive, manual steps between the apps you already use — intake, follow-up, reporting, and more.",
    heroPoints: [
      "Automate intake, routing, and follow-up",
      "Sync data between CRM, email, and scheduling tools",
      "Scheduled reports and reminders that never get forgotten",
    ],
    problemTitle: "Repetitive work is quietly expensive",
    problem: [
      "Re-typing data, chasing approvals, copying records between systems, and manual follow-up eat hours every week and introduce mistakes.",
      "That time is better spent on customers. The right automation removes the busywork without over-engineering.",
    ],
    scopeTitle: "What AICSNetworks automates",
    scope: [
      { title: "Intake & routing", body: "Capture requests from forms, email, or chat and route them to the right place automatically." },
      { title: "Ticketing & follow-up", body: "Create, assign, and follow up on tasks and tickets without manual steps." },
      { title: "CRM & email workflows", body: "Keep records updated and send the right message at the right time, automatically." },
      { title: "Reporting", body: "Scheduled reports and dashboards so you stop assembling numbers by hand." },
    ],
    useCasesTitle: "What it replaces",
    useCases: [
      { title: "Manual lead entry", body: "New inquiries land in your CRM with the right fields, automatically." },
      { title: "Follow-up reminders", body: "Customers and quotes get followed up on schedule without anyone remembering." },
      { title: "Status notifications", body: "Your team and customers get notified as work moves through each stage." },
      { title: "Weekly reporting", body: "The numbers you check every Monday show up on their own." },
    ],
    process: [
      { title: "Discovery", body: "We map your process and find the repetitive, rules-based steps." },
      { title: "Planning", body: "We prioritize the automations with the biggest time savings first." },
      { title: "Build & setup", body: "We connect your tools and build the automated workflows." },
      { title: "Testing", body: "We verify each flow with real cases so nothing fires incorrectly." },
      { title: "Launch & support", body: "We turn it on, monitor it, and refine as your process evolves." },
    ],
    localTitle: "Practical automation for Corona, CA businesses",
    local: [
      "AICSNetworks works hands-on with Corona, CA businesses to automate the right steps — and pairs automation with AI, custom software, and IT support where it adds the most value.",
    ],
    related: ["custom-software-development-corona", "ai-receptionist-corona", "it-support-corona"],
  },
  {
    slug: "it-support-corona",
    eyebrow: "IT support",
    h1: "Small Business IT Support in Corona, CA",
    title: "Small Business IT Support in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks provides small business IT support in Corona, CA — troubleshooting, device and email setup, software configuration, security, and ongoing help.",
    heroLead:
      "Spend your time on customers, not computer problems. AICSNetworks is the dependable IT support team for small businesses in Corona, CA — keeping your devices, email, and systems running.",
    heroPoints: [
      "Responsive troubleshooting and help-desk support",
      "Device, email, and software setup done right",
      "Backups, updates, and security fundamentals covered",
    ],
    problemTitle: "No IT person means lost time and risk",
    problem: [
      "Without dedicated IT, small teams lose hours to slow computers, login and email issues, and backups nobody checks — and security gaps go unnoticed until something breaks.",
      "You need a reliable team to call, plus the basics handled before they become emergencies.",
    ],
    scopeTitle: "What AICSNetworks provides",
    scope: [
      { title: "Troubleshooting & help desk", body: "A real person to call when something stops working — on-site or remote." },
      { title: "Device & software setup", body: "New computers, accounts, email, and software configured and ready to use." },
      { title: "Email & domain support", body: "Mailboxes, domains, and accounts set up and kept working." },
      { title: "Backups & security basics", body: "Backups, updates, and security fundamentals so problems don't become disasters." },
    ],
    useCasesTitle: "How small businesses use it",
    useCases: [
      { title: "Onboarding a new hire", body: "Get a new employee's computer, accounts, and email ready on day one." },
      { title: "Recurring help desk", body: "A go-to team for the day-to-day issues that interrupt work." },
      { title: "Email & domain trouble", body: "Fix delivery problems and get mail and domains configured correctly." },
      { title: "Proactive maintenance", body: "Updates, backups, and checkups that prevent the next outage." },
    ],
    process: [
      { title: "Discovery", body: "We review your devices, accounts, and the issues slowing you down." },
      { title: "Planning", body: "We prioritize quick fixes and the maintenance that prevents problems." },
      { title: "Build & setup", body: "We set up devices, email, backups, and security fundamentals." },
      { title: "Testing", body: "We verify everything works for your team before we call it done." },
      { title: "Launch & support", body: "We stay available for help and keep your systems maintained." },
    ],
    localTitle: "On-site and responsive in Corona, CA",
    local: [
      "As a Corona, CA-based provider, AICSNetworks can help on-site when it's needed and remotely when it's faster — with a real person who knows your setup.",
    ],
    related: ["network-installation-corona", "workflow-automation-corona", "custom-software-development-corona"],
  },
  {
    slug: "network-installation-corona",
    eyebrow: "Networking",
    h1: "Network Installation in Corona, CA",
    title: "Network Installation in Corona, CA | AICSNetworks",
    description:
      "AICSNetworks designs and installs business networks in Corona, CA — Wi-Fi, cabling, TCP/IP setup, routers, switches, access points, and VLANs that hold up.",
    heroLead:
      "Wi-Fi and wiring that hold up under real-world load. AICSNetworks designs and installs reliable business networks for companies in Corona, CA — done properly, with business-grade gear.",
    heroPoints: [
      "Survey-driven access point placement and cabling",
      "TCP/IP setup, routers, switches, and VLANs where needed",
      "Guest and staff separation, validated before we finish",
    ],
    problemTitle: "Consumer gear and improvised wiring don't hold up",
    problem: [
      "Dropped Wi-Fi, dead zones, mixed guest and staff traffic, and improvised wiring disrupt daily work and create security gaps.",
      "Busy offices and shops need a network designed for the space — not a stack of consumer routers.",
    ],
    scopeTitle: "What AICSNetworks installs",
    scope: [
      { title: "Wi-Fi & cabling", body: "Survey-driven access point placement and structured cabling for full, stable coverage." },
      { title: "TCP/IP & routing", body: "Routers, switches, and TCP/IP configuration set up cleanly and documented." },
      { title: "VLANs & segmentation", body: "Guest, staff, and device separation where it's needed for security and performance." },
      { title: "Servers & validation", body: "Server setup where required, plus performance validation before we call it done." },
    ],
    useCasesTitle: "Typical installs",
    useCases: [
      { title: "New office or shop", body: "Design and install the network from cabling to Wi-Fi before you open." },
      { title: "Fixing dead zones", body: "Re-plan access points and wiring so coverage holds across the whole space." },
      { title: "Guest + staff separation", body: "Keep visitor traffic isolated from your business systems." },
      { title: "Growth & added devices", body: "Scale the network as you add staff, devices, or locations." },
    ],
    process: [
      { title: "Discovery", body: "We survey the space and learn how the network needs to perform." },
      { title: "Planning", body: "We design AP placement, cabling, addressing, and segmentation." },
      { title: "Build & setup", body: "We install and configure the gear, cabling, and TCP/IP setup." },
      { title: "Testing", body: "We validate coverage, throughput, and segmentation across the space." },
      { title: "Launch & support", body: "We hand off a documented network and support changes as you grow." },
    ],
    localTitle: "Installed and supported in Corona, CA",
    local: [
      "AICSNetworks installs and supports networks for businesses in Corona, CA, and stays available for changes as you add staff, devices, or locations.",
    ],
    related: ["it-support-corona", "custom-software-development-corona", "business-website-development-corona"],
  },
];

export function getCoronaService(slug) {
  return coronaServices.find((s) => s.slug === slug) ?? null;
}

export function coronaServiceLinks() {
  return coronaServices.map((s) => ({ slug: s.slug, h1: s.h1, eyebrow: s.eyebrow }));
}
